// ================================================================
// Ländervorwahlen für Telefon-/WhatsApp-Empfängerfelder.
// ================================================================
// Bewusst eine kurze, kuratierte Liste (Schweiz zuerst, dann Nachbarn und die
// häufigsten Herkunftsländer der Kundschaft) statt aller ~240 ITU-Vorwahlen —
// das Feld lebt auf dem Handy, ein 240-Zeilen-Dropdown hilft niemandem.
// Namen nativ (wie languages.js), keine i18n-Keys nötig.

// @ts-expect-error — utils/phone.js ist reines JS ohne Typen
import { DEFAULT_DIAL_CODE } from '../../utils/phone.js';

export interface CountryCode {
  dial: string; // Vorwahl OHNE «+», z.B. '41'
  flag: string;
  name: string;
}

/**
 * Heimmarkt-Vorwahl. Der Wert steht in `utils/phone.js` — bis 2026-08-06 gab
 * es ihn hier UND dort, also zwei Wahrheiten über dieselbe Frage. Genau daran
 * lief der WhatsApp-Link auseinander.
 */
export const DEFAULT_DIAL: string = DEFAULT_DIAL_CODE;

export const COUNTRY_CODES: CountryCode[] = [
  { dial: '41', flag: '🇨🇭', name: 'Schweiz' },
  { dial: '49', flag: '🇩🇪', name: 'Deutschland' },
  { dial: '43', flag: '🇦🇹', name: 'Österreich' },
  { dial: '423', flag: '🇱🇮', name: 'Liechtenstein' },
  { dial: '33', flag: '🇫🇷', name: 'France' },
  { dial: '39', flag: '🇮🇹', name: 'Italia' },
  { dial: '34', flag: '🇪🇸', name: 'España' },
  { dial: '351', flag: '🇵🇹', name: 'Portugal' },
  { dial: '44', flag: '🇬🇧', name: 'United Kingdom' },
  { dial: '31', flag: '🇳🇱', name: 'Nederland' },
  { dial: '32', flag: '🇧🇪', name: 'België' },
  { dial: '48', flag: '🇵🇱', name: 'Polska' },
  { dial: '90', flag: '🇹🇷', name: 'Türkiye' },
  { dial: '355', flag: '🇦🇱', name: 'Shqipëria' },
  { dial: '383', flag: '🇽🇰', name: 'Kosova' },
  { dial: '389', flag: '🇲🇰', name: 'Makedonija' },
  { dial: '381', flag: '🇷🇸', name: 'Srbija' },
  { dial: '385', flag: '🇭🇷', name: 'Hrvatska' },
  { dial: '387', flag: '🇧🇦', name: 'Bosna i Hercegovina' },
  { dial: '386', flag: '🇸🇮', name: 'Slovenija' },
  { dial: '380', flag: '🇺🇦', name: 'Ukraïna' },
  { dial: '98', flag: '🇮🇷', name: 'Iran' },
  { dial: '1', flag: '🇺🇸', name: 'USA / Canada' },
];

// Vorwahl-Match: längste zuerst (423 vor 43 vor 4…), sonst gewinnt die falsche.
const DIALS_BY_LENGTH = [...COUNTRY_CODES.map((c) => c.dial)].sort((a, b) => b.length - a.length);

/**
 * Zerlegt eine gespeicherte/getippte Nummer in Vorwahl + nationalen Teil.
 * Versteht die drei Formen, die bei uns real vorkommen:
 *   «+41 79 123 45 67» / «0041…»  → explizit international
 *   «41791234567»                 → Contact.phone (WhatsApp-normalisiert)
 *   «079 123 45 67»               → national → Default Schweiz
 * Unbekannte Vorwahl → Default Schweiz, Nummer unverändert lassen (nichts
 * abschneiden, was wir nicht verstehen).
 */
export function splitPhone(raw: string | null | undefined): { dial: string; national: string } {
  const trimmed = (raw || '').trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return { dial: DEFAULT_DIAL, national: '' };

  const explicitIntl = trimmed.startsWith('+') || digits.startsWith('00');
  const intlDigits = explicitIntl ? digits.replace(/^00/, '') : digits;

  // national getippt («079…») — nur wenn nicht explizit international
  if (!explicitIntl && digits.startsWith('0')) {
    return { dial: DEFAULT_DIAL, national: digits.replace(/^0+/, '') };
  }

  const dial = DIALS_BY_LENGTH.find((d) => intlDigits.startsWith(d));
  // Ohne erkennbare Vorwahl ist eine kurze Ziffernfolge fast sicher ein
  // nationaler Rumpf («791234567») — nicht als Vorwahl+Rest fehldeuten.
  if (!dial || (!explicitIntl && intlDigits.length <= 9)) {
    return { dial: DEFAULT_DIAL, national: intlDigits };
  }
  return { dial, national: intlDigits.slice(dial.length) };
}

/** Vorwahl + nationaler Teil → volle Ziffernfolge («41791234567»). */
export function joinPhone(dial: string, national: string): string {
  const nat = national.replace(/\D/g, '').replace(/^0+/, '');
  return nat ? dial + nat : '';
}
